import { createContext, useContext, useState, useCallback, useMemo, useEffect, ReactNode } from 'react'

// Types
export interface AuthUser {
  id: string
  email: string
  name: string
  given_name?: string
  family_name?: string
  preferred_username?: string
  email_verified?: boolean
  roles?: string[]
  groups?: string[]
  package?: 'basic' | 'business' | 'enterprise'
}

export interface AuthTokens {
  accessToken: string
  refreshToken?: string
  idToken?: string
  expiresAt: number
}

export interface AuthState {
  isAuthenticated: boolean
  isLoading: boolean
  user: AuthUser | null
  tokens: AuthTokens | null
  error: string | null
}

export interface AuthContextValue extends AuthState {
  login: () => void
  logout: () => void
  getAccessToken: () => string | null
  refreshTokens: () => Promise<void>
}

// Default context value
const defaultAuthContext: AuthContextValue = {
  isAuthenticated: false,
  isLoading: true,
  user: null,
  tokens: null,
  error: null,
  login: () => {},
  logout: () => {},
  getAccessToken: () => null,
  refreshTokens: async () => {},
}

// Context
export const AuthContext = createContext<AuthContextValue>(defaultAuthContext)

// Hook
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  
  return context
}

// Authenticated API hook
export function useAuthenticatedFetch() {
  const { getAccessToken, isAuthenticated } = useAuth()

  const authFetch = useCallback(async (url: string, options: RequestInit = {}): Promise<Response> => {
    if (!isAuthenticated) {
      throw new Error('Not authenticated')
    }

    const token = getAccessToken()
    if (!token) {
      throw new Error('No access token available')
    }

    const headers = new Headers(options.headers)
    headers.set('Authorization', `Bearer ${token}`)
    headers.set('Content-Type', 'application/json')

    return fetch(url, {
      ...options,
      headers,
    })
  }, [getAccessToken, isAuthenticated])

  return authFetch
}

// Auth Provider Props
export interface AuthProviderProps {
  children: ReactNode
  config: {
    authority: string
    clientId: string
    redirectUri: string
    scope?: string
  }
}

// PKCE Utilities
function generateCodeVerifier(): string {
  const array = new Uint8Array(32)
  crypto.getRandomValues(array)
  return base64UrlEncode(array)
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder()
  const data = encoder.encode(verifier)
  const hash = await crypto.subtle.digest('SHA-256', data)
  return base64UrlEncode(new Uint8Array(hash))
}

function base64UrlEncode(array: Uint8Array): string {
  const base64 = btoa(String.fromCharCode(...array))
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function parseJwt(token: string): Record<string, unknown> {
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(jsonPayload)
  } catch {
    return {}
  }
}

// Storage keys
const CODE_VERIFIER_KEY = 'auth_code_verifier'
const TOKENS_KEY = 'auth_tokens'

// Auth Provider Component
export function AuthProvider({ children, config }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    tokens: null,
    error: null,
  })

  // Parse user from token
  const parseUserFromToken = useCallback((accessToken: string): AuthUser | null => {
    const payload = parseJwt(accessToken)
    
    if (!payload.sub) return null

    return {
      id: payload.sub as string,
      email: (payload.email as string) || '',
      name: (payload.name as string) || (payload.preferred_username as string) || '',
      given_name: payload.given_name as string,
      family_name: payload.family_name as string,
      preferred_username: payload.preferred_username as string,
      email_verified: payload.email_verified as boolean,
      roles: (payload.realm_access as { roles?: string[] })?.roles || [],
      groups: payload.groups as string[],
      package: (payload.package as AuthUser['package']) || 'basic',
    }
  }, [])

  // Get access token
  const getAccessToken = useCallback((): string | null => {
    return state.tokens?.accessToken || null
  }, [state.tokens])

  // Login
  const login = useCallback(async () => {
    const codeVerifier = generateCodeVerifier()
    const codeChallenge = await generateCodeChallenge(codeVerifier)

    // Store verifier
    sessionStorage.setItem(CODE_VERIFIER_KEY, codeVerifier)

    // Build auth URL
    const params = new URLSearchParams({
      client_id: config.clientId,
      redirect_uri: config.redirectUri,
      response_type: 'code',
      scope: config.scope || 'openid profile email',
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    })

    window.location.href = `${config.authority}/protocol/openid-connect/auth?${params}`
  }, [config])

  // Logout
  const logout = useCallback(() => {
    sessionStorage.removeItem(TOKENS_KEY)
    sessionStorage.removeItem(CODE_VERIFIER_KEY)

    setState({
      isAuthenticated: false,
      isLoading: false,
      user: null,
      tokens: null,
      error: null,
    })

    // Redirect to Keycloak logout
    const params = new URLSearchParams({
      client_id: config.clientId,
      post_logout_redirect_uri: window.location.origin,
    })

    window.location.href = `${config.authority}/protocol/openid-connect/logout?${params}`
  }, [config])

  // Refresh tokens
  const refreshTokens = useCallback(async () => {
    if (!state.tokens?.refreshToken) {
      throw new Error('No refresh token available')
    }

    const response = await fetch(`${config.authority}/protocol/openid-connect/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: config.clientId,
        refresh_token: state.tokens.refreshToken,
      }),
    })

    if (!response.ok) {
      throw new Error('Failed to refresh tokens')
    }

    const data = await response.json()
    const newTokens: AuthTokens = {
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
      idToken: data.id_token,
      expiresAt: Date.now() + data.expires_in * 1000,
    }

    const user = parseUserFromToken(data.access_token)

    sessionStorage.setItem(TOKENS_KEY, JSON.stringify(newTokens))

    setState({
      isAuthenticated: true,
      isLoading: false,
      user,
      tokens: newTokens,
      error: null,
    })
  }, [state.tokens, config, parseUserFromToken])

  // Handle callback
  useEffect(() => {
    const handleCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search)
      const code = urlParams.get('code')
      const error = urlParams.get('error')

      if (error) {
        setState({
          isAuthenticated: false,
          isLoading: false,
          user: null,
          tokens: null,
          error: urlParams.get('error_description') || error,
        })
        return
      }

      if (code) {
        const codeVerifier = sessionStorage.getItem(CODE_VERIFIER_KEY)

        if (!codeVerifier) {
          setState({
            isAuthenticated: false,
            isLoading: false,
            user: null,
            tokens: null,
            error: 'Missing code verifier',
          })
          return
        }

        try {
          const response = await fetch(`${config.authority}/protocol/openid-connect/token`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams({
              grant_type: 'authorization_code',
              client_id: config.clientId,
              redirect_uri: config.redirectUri,
              code,
              code_verifier: codeVerifier,
            }),
          })

          if (!response.ok) {
            throw new Error('Token exchange failed')
          }

          const data = await response.json()
          const tokens: AuthTokens = {
            accessToken: data.access_token,
            refreshToken: data.refresh_token,
            idToken: data.id_token,
            expiresAt: Date.now() + data.expires_in * 1000,
          }

          const user = parseUserFromToken(data.access_token)

          sessionStorage.setItem(TOKENS_KEY, JSON.stringify(tokens))
          sessionStorage.removeItem(CODE_VERIFIER_KEY)

          // Clear URL params
          window.history.replaceState({}, document.title, window.location.pathname)

          setState({
            isAuthenticated: true,
            isLoading: false,
            user,
            tokens,
            error: null,
          })
        } catch (err) {
          setState({
            isAuthenticated: false,
            isLoading: false,
            user: null,
            tokens: null,
            error: err instanceof Error ? err.message : 'Authentication failed',
          })
        }
        return
      }

      // Check for existing tokens
      const storedTokens = sessionStorage.getItem(TOKENS_KEY)
      if (storedTokens) {
        try {
          const tokens: AuthTokens = JSON.parse(storedTokens)
          
          if (tokens.expiresAt > Date.now()) {
            const user = parseUserFromToken(tokens.accessToken)
            setState({
              isAuthenticated: true,
              isLoading: false,
              user,
              tokens,
              error: null,
            })
            return
          }
        } catch {
          sessionStorage.removeItem(TOKENS_KEY)
        }
      }

      setState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        tokens: null,
        error: null,
      })
    }

    handleCallback()
  }, [config, parseUserFromToken])

  // Auto refresh
  useEffect(() => {
    if (!state.tokens?.expiresAt) return

    const timeUntilExpiry = state.tokens.expiresAt - Date.now()
    const refreshTime = timeUntilExpiry - 60000 // Refresh 1 minute before expiry

    if (refreshTime <= 0) return

    const timer = setTimeout(() => {
      refreshTokens().catch(console.error)
    }, refreshTime)

    return () => clearTimeout(timer)
  }, [state.tokens, refreshTokens])

  const contextValue = useMemo<AuthContextValue>(() => ({
    ...state,
    login,
    logout,
    getAccessToken,
    refreshTokens,
  }), [state, login, logout, getAccessToken, refreshTokens])

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  )
}
